import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, Dropzone, EmptyState, Figure, Label, Notice, cx } from '@raro/ui';
import { useState } from 'react';
import { api } from '../lib/api';
import type { ImageRef, Source } from '../lib/types';

/**
 * Escolher uma foto: da galeria do projeto (ou da organização) ou enviando novas.
 * Toda foto enviada vira uma fonte do projeto (entra na Parede e na Machine) — não existe imagem solta.
 */
export function ImagePicker({ open, onClose, organizationId, projectId, current, onPick, title = 'escolher imagem', eyebrow = 'Imagem' }: { open: boolean; onClose: () => void; organizationId: string; projectId?: string | null; current?: string | null; onPick: (fileId: string | null) => void; title?: string; eyebrow?: string }) {
  const qc = useQueryClient();
  const key = projectId ? ['images', 'project', projectId] : ['images', 'org', organizationId];
  const gallery = useQuery({ queryKey: key, queryFn: () => api.get<(ImageRef & { projectId: string | null })[]>(projectId ? `/projects/${projectId}/images` : `/organizations/${organizationId}/images`), enabled: open });
  const [files, setFiles] = useState<File[]>([]);
  const [selected, setSelected] = useState<string | null>(current ?? null);
  const upload = useMutation({
    mutationFn: async () => {
      const created: Source[] = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        if (projectId) form.append('projectId', projectId);
        created.push(await api.post<Source>(`/organizations/${organizationId}/sources`, form));
      }
      return created;
    },
    onSuccess: async (created) => {
      setFiles([]);
      await qc.invalidateQueries({ queryKey: ['images'] });
      await qc.invalidateQueries({ queryKey: ['sources'] });
      const last = created[created.length - 1];
      if (last?.fileId) setSelected(last.fileId);
    },
  });
  return (
    <Drawer open={open} onClose={onClose} eyebrow={eyebrow} title={title}>
      <div className="rr-stack rr-stack--5">
        <div className="rr-stack rr-stack--2">
          <Label>Enviar fotos</Label>
          <Dropzone files={files} onFiles={setFiles} accept="image/*" />
          {files.length > 0 && <div className="rr-row"><Button size="sm" variant="primary" onClick={() => upload.mutate()} disabled={upload.isPending}>{upload.isPending ? 'enviando…' : `enviar ${files.length} foto(s)`}</Button><Button size="sm" variant="quiet" onClick={() => setFiles([])}>limpar</Button></div>}
          {upload.isError && <Notice tone="error">{(upload.error as Error).message}</Notice>}
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>cada foto enviada vira uma fonte {projectId ? 'do projeto' : 'da organização'}: entra na Parede e a Machine pode usá-la.</span>
        </div>
        <div className="rr-stack rr-stack--2">
          <Label>Galeria · {gallery.data?.length ?? 0}</Label>
          {gallery.isSuccess && gallery.data.length === 0 && <EmptyState title="nenhuma foto ainda.">envie acima, ou coloque fotos na Machine pelo projeto.</EmptyState>}
          <div className="rr-imgpick">
            {gallery.data?.map((img) => (
              <button key={img.id} type="button" className={cx('rr-imgpick__item', selected === img.id && 'rr-imgpick__item--on')} onClick={() => setSelected(selected === img.id ? null : img.id)} title={img.name}>
                <Figure src={img.thumbUrl ?? img.url} ratio="4 / 3" radius="sm" />
                <span className="rr-imgpick__name">{img.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="rr-row">
          <Button variant="primary" disabled={selected === (current ?? null)} onClick={() => { onPick(selected); onClose(); }}>usar esta imagem</Button>
          {current && <Button variant="quiet" onClick={() => { onPick(null); onClose(); }}>remover imagem</Button>}
          <Button variant="quiet" onClick={onClose}>cancelar</Button>
        </div>
      </div>
    </Drawer>
  );
}
